
import { useEffect, useRef, useState } from 'react'
import { Button, Col, Container, Row, Table } from 'react-bootstrap';
import arrayUtilities from '../../utilities/arrayUtilities';
import stringUtilities from '../../utilities/stringUtilities'
import dateUtilities from '../../utilities/dateUtilities';
import enums from '../../utilities/enums';
import useApi from '../../utilities/useApi';
import OrganisationSelector from '../common/organisationSelector';
import FilterBar from '../common/filterBar';
import Loading from '../common/loading';
import ErrorModal from '../common/errorModal';
import { DataPagination } from '../common/pagination';

const paginationSeedNumber = 100;

const formatDateTime = (date) => `${dateUtilities.formatToDateString(date)} ${dateUtilities.formatToTimeString(date)}`;

export default function Downloads(props) {

    const { api } = useApi();
    const [isLoading, setIsLoading] = useState(true);
    const [organisationId, setOrganisationId] = useState(props.user?.organisationalUnitId ?? null);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const [downloads, setDownloads] = useState(null);
    const [numberOfResultsPages, setNumberOfResultsPages] = useState(0);
    const [errorModal, setErrorModal] = useState(null);

    const getDownloadsAbortController = useRef(null);
    const fetchDownloads = (orgId, textFilter, pageNumber) => {

        if (stringUtilities.isNullOrEmpty(orgId)) {
            setDownloads(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        if (getDownloadsAbortController.current) {
            getDownloadsAbortController.current.abort();
        }

        getDownloadsAbortController.current = new AbortController();
        const signal = getDownloadsAbortController.current.signal;

        api.searchDownloads(
            orgId, textFilter, pageNumber, signal,
            (data) => {
                setDownloads(data);
                setNumberOfResultsPages(arrayUtilities.isNullOrEmpty(data.items) ? 0 : Math.ceil(data.totalCount / paginationSeedNumber));
                setIsLoading(false);
            },
            (error) => {
                setIsLoading(false);
                setErrorModal(error);
            }
        );
    };

    useEffect(() => {
        fetchDownloads(organisationId, filter, page);
    }, [organisationId, filter, page]);

    const download = (item) => {
        api.getDownload(item.organisationId, item.taskType, item.id, (result) => {
            if (result.url) {
                window.open(result.url, '_blank', 'noopener');
            } else {
                setErrorModal({ message: 'This file is not available to download.' });
            }
        }, (error) => setErrorModal(error));
    };

    return <Container className='pt-5 pb-lg-4 mt-5 mb-sm-2 px-4 report downloads' fluid>
        <Row>
            <Col className='mb-5'>
                <h1 className='h1 mt-3'>Reports</h1>
                <div className='d-flex justify-content-between align-items-start my-5 flex-wrap gap-3'>
                    <h2 className="h2 m-0">Downloads</h2>
                    <div className='d-flex justify-content-end flex-wrap gap-2'>
                        <FilterBar value={filter} onChange={(value) => { setFilter(value); setPage(0); }} className="me-2" />
                        <OrganisationSelector value={organisationId} organisations={props.organisationalUnits} onChange={(id) => { setOrganisationId(id); setPage(0); }} />
                    </div>
                </div>

                {isLoading && <Loading />}
                {!isLoading &&
                    <>
                        <Table responsive hover sm>
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Project</th>
                                    <th>User</th>
                                    <th>Created</th>
                                    <th>Completed</th>
                                    <th>Status</th>
                                    <th>File</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!arrayUtilities.isNullOrEmpty(downloads?.items) && downloads.items.map((item) => <tr key={`download-${item.id}`}>
                                    <td>{enums.ExportTaskType[item.taskType]}</td>
                                    <td>{item.projectName}</td>
                                    <td>{item.userName}</td>
                                    <td>{formatDateTime(item.createdAt)}</td>
                                    <td>{item.completedAt ? formatDateTime(item.completedAt) : '–'}</td>
                                    <td>
                                        <span className={enums.ExportStatusTextClass[item.status]}>{enums.ExportStatus[item.status]}</span>
                                    </td>
                                    <td>
                                        {item.status === 2 && item.blobName
                                            ? <Button variant='link' className='p-0' onClick={() => download(item)}><i className='fi-download me-2'></i>{item.blobName}</Button>
                                            : <span className='text-muted'>–</span>
                                        }
                                    </td>
                                </tr>)}
                            </tbody>
                        </Table>
                        {arrayUtilities.isNullOrEmpty(downloads?.items) &&
                            <div className='text-muted text-center py-5'>No downloads match the current filters.</div>
                        }
                        {downloads && !arrayUtilities.isNullOrEmpty(downloads.items) &&
                            <div className='d-flex justify-content-center'>
                                <DataPagination
                                    totalResults={downloads.totalCount}
                                    page={page}
                                    onPageNumberClick={(pageNumber) => setPage(pageNumber)}
                                    paginationSeedNumber={paginationSeedNumber}
                                    numberOfResultsPages={numberOfResultsPages}
                                />
                            </div>
                        }
                    </>
                }
            </Col>
        </Row>

        {errorModal &&
            <ErrorModal error={errorModal} onDismiss={() => setErrorModal(null)} />
        }
    </Container>;
}
